import _toConsumableArray from 'babel-runtime/helpers/toConsumableArray';
import queryString from 'query-string';

/**
 * Get New Active Filters
 *
 * 1. Get key from newFilter (data--color_type)
 * 2. if currentFilters has key and filter, remove it
 *      if newKey contains 0 items, also remove
 *    else add new filter to active filters object
 *      if newKey is color or subcategory
 *          push to array (mult filters)
 *      else
 *        overwrite array with the newFilter(single filter)
 * 3. return newActiveFilters object
 *
 * @param {Object} currentFilters - all current active filters
 * @param {String} newFilter - new filter(product tag) ie `data--color_type:blue`
 * @return {Object} returns object with new filter added or removed from current filters
 */
export var getActiveFilters = function getActiveFilters(filterAry) {
  var activeFitlers = {};
  var _iteratorNormalCompletion = true;
  var _didIteratorError = false;
  var _iteratorError = undefined;

  try {
    for (var _iterator = filterAry[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
      var filter = _step.value;

      var key = getFilterKey(filter);
      if (activeFitlers.hasOwnProperty(key)) {
        activeFitlers[key].push(filter);
      } else {
        activeFitlers[key] = [filter];
      }
    }
  } catch (err) {
    _didIteratorError = true;
    _iteratorError = err;
  } finally {
    try {
      if (!_iteratorNormalCompletion && _iterator.return) {
        _iterator.return();
      }
    } finally {
      if (_didIteratorError) {
        throw _iteratorError;
      }
    }
  }

  return activeFitlers;
};

/**
 * Get Filtered Products
 *
 * 1. Loop through active filters and get all products that match that filter
 * 2. Push into an array of product arrays
 * 3. If more than one array or product arrays,
 *     reduce the arrays checking if the  product exists in all arrays
 * 4. If only a single product array, flatten and return
 *
 * @param {Object} activeFilters - all Active Filters
 * @param {Array} allProducts - all products in collection
 * @return {Array} filteredProducts - returns array of products that match active filters
 */
export var getFilteredProducts = function getFilteredProducts(activeFilters, allProducts) {
  // if allProducts fetch is not complete, return empty array
  if (allProducts.length === 0) return [];

  var arrayOfProductArys = [];
  Object.keys(activeFilters).map(function (filter) {
    if (activeFilters[filter].length) {
      var filteredProducts = allProducts.filter(function (product) {
        var tags = product.node.tags;

        if (activeFilters[filter].some(function (f) {
          return tags.includes(f);
        })) {
          return product;
        }
      });
      arrayOfProductArys.push(filteredProducts);
    }
  });
  return arrayOfProductArys.length > 1 ? arrayOfProductArys.reduce(function (a, arr) {
    return a.filter(function (num) {
      return arr.includes(num);
    });
  }) : [].concat.apply([], arrayOfProductArys);
};

/**
 * Sort Products By user selection
 *
 * @param {Array} products - array of all products
 * @param {String} sortBy - string passed by users selection of sortby select value
 * @return {Array} sortedProductAry - returns sorted product array
 */
export var getProductsToDisplay = function getProductsToDisplay(activeFilterCount, products, filteredProducts, sortBy, productsPerPage, pageIndex) {
  var productsToDisplay = activeFilterCount > 0 ? [].concat(_toConsumableArray(filteredProducts)) : [].concat(_toConsumableArray(products));

  var first = pageIndex * productsPerPage;
  var last = first + productsPerPage;

  switch (sortBy) {
    case 'price-asc':
      productsToDisplay.sort(function (a, b) {
        return parseInt(a.node.priceRange.minVariantPrice.amount) - parseInt(b.node.priceRange.minVariantPrice.amount);
      });
      break;
    case 'price-desc':
      productsToDisplay.sort(function (a, b) {
        return parseInt(b.node.priceRange.minVariantPrice.amount) - parseInt(a.node.priceRange.minVariantPrice.amount);
      });
      break;
    case 'created-at':
      productsToDisplay.sort(function (a, b) {
        return new Date(b.node.createdAt) - new Date(a.node.createdAt);
      });
      break;
    default:
      // basically case: 'best-selling' (return original products array)
      productsToDisplay;
  }

  return productsToDisplay.slice(first, last);
};

/**
 * Get filter Key
 *
 * @param {String} filter - ie `data--color_type:blue`
 * @return {String} key - returns `data--color_type`
 */
export var getFilterKey = function getFilterKey(filter) {
  return filter.split(':')[0];
};

/**
 * Get filter Key
 *
 * @param {String} filter - ie `data--color_type:blue`
 * @return {String} key - returns `blue`
 */
export var getFilterValue = function getFilterValue(filter) {
  return filter.split(':')[1];
};

/**
 * Get Pagination Array
 *
 * @param {Integer} currentPageIndex - page index user is currently on
 * @param {Integer} totalPages - total pages for given collection
 * @return {Array} paginationAry - returns pagination array
 */
export var getPaginationAry = function getPaginationAry(currentPageIndex, totalPages) {
  var paginationAry = [];
  for (var i = 0; i < totalPages; i++) {
    if (i === 0 || // if first one
    i === totalPages - 1 || // or last one
    i <= currentPageIndex + 2 && i >= currentPageIndex - 2 // or within 2 of current page
    ) {
        paginationAry.push(i);
      } else if (paginationAry[paginationAry.length - 1] === '...') {
      // if last item is ..., skip
      continue;
    } else {
      paginationAry.push('...');
    }
  }
  return paginationAry;
};

/**
 * Make Filter Query An Array
 *
 * query string does not return an array
 * so I made this to return an array if it is not already one
 *
 * @param {Object} query - query object
 * @return {Object} query - returns same object with filter as an array
 */
export var makeFilterQueryAnArray = function makeFilterQueryAnArray(query) {
  if ('filter' in query) {
    query.filter = Array.isArray(query.filter) ? query.filter : [query.filter];
  }
  return query;
};

/**
 * Allow Only One Size or Gender Filter
 *
 * Size and Gender should only allow one filter. so this will remove
 * any extra filters and force there to only be the one.
 *
 * @param {Array} ary - array of filters
 * @return {Array} ary - same array with only one size/gender filter
 */
export var allowOnlyOneSizeGenderFilter = function allowOnlyOneSizeGenderFilter(ary, newItem) {
  var sizeFilters = ary.filter(function (item) {
    return item.includes('data--size');
  });
  var genderFilters = ary.filter(function (item) {
    return item.includes('data--gender');
  });
  var removeSize = void 0,
      removeGender = void 0;
  if (sizeFilters.length > 1) {
    removeSize = sizeFilters.filter(function (item) {
      return item !== newItem;
    })[0];
    ary = ary.filter(function (item) {
      return item !== removeSize;
    });
  }
  if (genderFilters.length > 1) {
    removeGender = genderFilters.filter(function (item) {
      return item !== newItem;
    })[0];
    ary = ary.filter(function (item) {
      return item !== removeGender;
    });
  }
  return ary;
};

/**
 * Get Sub Menu
 *
 * Using the main menu and current collection url
 * Get the sub menu for given collection
 *
 * @param {Array} mainMenu
 * @return {Array} subMenu
 */
export var getSubMenu = function getSubMenu(mainMenu) {
  var currentCollectionUrl = window.location.pathname;
  var subMenu = void 0;
  mainMenu.map(function (parent) {
    if (currentCollectionUrl === parent.url) {
      subMenu = parent.bottomLinks;
    }
    parent.bottomLinks.map(function (child) {
      if (currentCollectionUrl === child.url) {
        subMenu = parent.bottomLinks;
      }
    });
    parent.topLinks.map(function (child) {
      if (currentCollectionUrl === child.url) {
        subMenu = parent.topLinks;
      }
    });
  });
  return subMenu;
};

/**
 * Build Query String
 *
 * After manipulating the query params, we need to return
 * the stringified version to be used in the url.
 * 1. Get the new query.
 * 2. make filter query an array
 * 3. Check if new param is a filter or page
 *    Filter:
 *      Add or remove new filter
 *      Check if it is gender or size and only allow one filter
 *        else add the new filter to the array
 *    Page:
 *      if new page query, Add it
 * 4. Return stringified version to be used as url param
 *
 * @param {Object} query - object of query params
 * @return {String} newQueryString - string of query params
 */
export var buildQueryString = function buildQueryString(query, newParam) {
  var newQuery = Object.assign({}, query);
  newQuery = makeFilterQueryAnArray(newQuery);
  if ('filter' in newQuery && 'filter' in newParam) {
    //add or remove new filter
    if (newQuery.filter.includes(newParam.filter)) {
      newQuery.filter = newQuery.filter.filter(function (item) {
        return item !== newParam.filter;
      });
    } else {
      newQuery.filter.push(newParam.filter);
    }
  } else if ('filter' in newParam) {
    // create new array of filters
    newQuery.filter = [newParam.filter];
  }
  if ('filter' in newQuery) {
    newQuery.filter = allowOnlyOneSizeGenderFilter(newQuery.filter, newParam.filter);
    // remove page query to default to page 1
    delete newQuery.page;
  }

  // set up sort params
  if ('sort' in newQuery || 'sort' in newParam) {
    newQuery.sort = newParam.sort;
  }

  if ('page' in newQuery || 'page' in newParam) {
    // if (newQuery.page === '1') {delete newQuery.page;
    if (newParam.page === '1') {
      delete newParam.page;
      delete newQuery.page;
    } else newQuery.page = newParam.page ? newParam.page : newQuery.page;
  }

  var newQueryString = queryString.stringify(newQuery);
  return newQueryString;
};